import styled from '@emotion/styled';
import { ReactNode } from 'react';

const VisuallyHiddenCaption = styled.caption`
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  whitespace: nowrap;
  width: 1px;

  &:focus {
    clip: initial;
    clippath: initial;
    height: initial;
    overflow: initial;
    position: initial;
    whitespace: initial;
    width: initial;
  }
`;

export const DataTable = ({
  caption,
  className,
  data,
  headers
}: {
  caption: string;
  className?: string;
  data: { [key: string]: ReactNode }[];
  headers: { [key: string]: ReactNode };
}) => (
  <table className={className}>
    <VisuallyHiddenCaption>{caption}</VisuallyHiddenCaption>
    <thead>
      <tr>
        {Object.entries(headers).map(([key, label], i) => (
          <th
            key={key}
            scope="col"
            style={{
              zIndex: Object.keys(headers).length + 2 - i
            }}
          >
            {label}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {data.map((d, index) => (
        <tr key={index}>
          {Object.keys(headers).map((key) => (
            <td key={key}>{d[key] || ''}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

export type DataTable2Cell = {
  colSpan?: number;
  cell: React.ReactNode;
  key?: string;
};

export type DataTable2Row<OtherData extends any = undefined> = {
  key?: string;
  cells: DataTable2Cell[];
  // This can be incredibly useful to pass other data like match or sort data to it.
  // Imagine a scenario where you have a useMemo which does some heavy lifting in
  // computing these rows
  // If you add a search interface, every keystroke would need to recompute this.
  // By splitting out the match data, the first hook can only react to actual data
  // changes, and a second hook can deal with the filtering.
  otherData: OtherData;
};

const DataTable2RowComponent = <OtherData extends any = undefined>({
  row
}: {
  row: DataTable2Row<OtherData>;
}) => {
  return (
    <tr>
      {row.cells.map(({ colSpan, cell, key }, i) => (
        <td colSpan={colSpan === undefined ? 1 : colSpan} key={key || i}>
          {cell}
        </td>
      ))}
    </tr>
  );
};

export const DataTable2 = <OtherData extends any = undefined>({
  caption,
  className,
  headers,
  rows
}: {
  caption: string;
  className?: string;
  headers?: DataTable2Cell[];
  rows: DataTable2Row<OtherData>[];
}) => {
  return (
    <table className={className}>
      <VisuallyHiddenCaption>{caption}</VisuallyHiddenCaption>
      {headers && (
        <thead>
          <tr>
            {headers.map(({ colSpan, cell, key }, i) => (
              <th
                colSpan={colSpan === undefined ? 1 : colSpan}
                key={key || i}
                scope="col"
                style={{
                  zIndex: headers.length + 2 - i
                }}
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {rows.map((row, i) => (
          <DataTable2RowComponent row={row} key={row.key || i} />
        ))}
      </tbody>
    </table>
  );
};

export const DataTable2With2StickyColumns = styled(DataTable2)<{
  sticky: {
    paddingRight: number;
    firstColumnWidth: number;
  };
}>`
  width: auto !important;
  border-collapse: collapse;
  white-space: nowrap;

  td:first-of-type,
  th:first-of-type {
    padding-inline-end: ${(p) => p.theme.spacing(p.sticky.paddingRight)}px;
    position: sticky;
    left: 0;
    z-index: 1;
    background-color: white;
    white-space: normal;
  }

  td:nth-child(2),
  th:nth-child(2) {
    padding-inline-end: ${(p) => p.theme.spacing(p.sticky.paddingRight)}px;
    position: sticky;
    left: ${
      (p) =>
        p.theme.spacing(p.sticky.firstColumnWidth) +
        p.theme.spacing(p.sticky.paddingRight) +
        1 // Anchor column left padding
    }px;
    z-index: 1;
    background-color: white;
    text-align: end;
  }
`;
